import { forwardRef, ReactNode, FocusEvent, useState } from 'react'

import { cva, VariantProps } from 'class-variance-authority'
import { MaskedNumber } from 'imask'

import { formatAmount } from '@erudilabs/format-utils'

import { mergeClassName } from '@erudilabs/merge-class-utils'

const amount = cva(
  [
    'border w-full h-10 rounded-md text-sm pr-4 placeholder-gray-600',
    'bg-white border-gray-300 text-black focus:outline-blue-500',
    'dark:bg-gray-900 dark:border-gray-700 dark:text-white dark:focus:outline-blue-300',
  ],
  {
    variants: {
      disabled: {
        true: [
          'cursor-not-allowed',
          'bg-gray-200 border-gray-300 text-gray-600 hover:bg-gray-200 disabled:placeholder-gray-500',
          'dark:bg-gray-800 dark:border-gray-800 dark:text-gray-600 dark:hover:bg-gray-800 dark:disabled:placeholder-gray-700',
        ],
      },
    },
    defaultVariants: {
      disabled: false,
    },
  }
)

export type CurrencyProps = 'RP' | 'PHP'

export interface AmountInputProps extends VariantProps<typeof amount> {
  className?: string
  testId?: string
  label?: string
  name: string
  id?: string
  helper?: ReactNode
  defaultValue?: string
  currency: CurrencyProps
  max?: number
  value?: string
  readOnly?: boolean
  placeholder?: string
  autoFocus?: boolean
  errorMessage?: string
  hideErrorMessage?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (e: any) => void
  onBlur?: (e: FocusEvent) => void
}

export const AmountInput = forwardRef<HTMLInputElement, AmountInputProps>(
  (props, ref) => {
    const {
      readOnly,
      name,
      testId,
      id,
      onChange,
      onBlur,
      max,
      defaultValue,
      value,
      errorMessage,
      hideErrorMessage,
      placeholder,
      autoFocus,
      currency,
      helper,
      label,
      disabled,
      className,
    } = props

    const addDecimal = (str: string) => {
      return str.slice(0, -2) + '.' + str.slice(-2)
    }

    const maskInputAmount = (str?: string | null) => {
      if (!str) return undefined

      const maskingPHP = new MaskedNumber({
        mask: Number,
        normalizeZeros: false,
        signed: false,
        radix: '.',
        mapToRadix: [','],
        thousandsSeparator: ',',
        max: max,
      })

      const maskingRP = new MaskedNumber({
        mask: Number,
        normalizeZeros: false,
        signed: false,
        thousandsSeparator: '.',
        max: max,
      })

      if (currency === 'PHP') {
        return maskingPHP.resolve(
          str !== '' && str.length > 1
            ? addDecimal(str.replace(/[.,]/g, ''))
            : str
        )
      } else {
        return maskingRP.resolve(str)
      }
    }

    const [maskedValue, setMaskedValue] = useState<number | string>(
      formatAmount((value as string) || '', {
        countryCode: currency === 'PHP' ? 'PH' : 'ID',
        removePrefix: true,
      }) ||
        formatAmount((defaultValue as string) || '', {
          countryCode: currency === 'PHP' ? 'PH' : 'ID',
          removePrefix: true,
        }) ||
        ''
    )

    const handleChange = (str: string) => {
      const masked = maskInputAmount(
        currency === 'RP' ? str.replace(/^0+/, '') : str.replace(/^0.00+/, '')
      )
      onChange?.(masked)
      setMaskedValue(masked || '')
    }

    return (
      <div className={mergeClassName('relative', className)}>
        <div
          className={mergeClassName(
            'absolute left-4 font-semibold text-gray-600 dark:text-gray-500',
            label ? 'top-[38px]' : 'top-1/2 -translate-y-1/2',
            disabled && 'dark:text-gray-700'
          )}
        >
          {currency === 'RP' ? 'Rp.' : '₱'}
        </div>

        {label && (
          <label
            htmlFor={name}
            className={mergeClassName(
              errorMessage
                ? 'text-red-600 dark:text-red-400'
                : 'text-black dark:text-white',
              'mb-2 block cursor-pointer text-left font-semibold'
            )}
          >
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={id}
          readOnly={readOnly}
          data-testid={testId}
          value={maskedValue}
          name={name}
          autoFocus={autoFocus}
          placeholder={placeholder}
          type="tel"
          className={mergeClassName(
            amount({
              disabled,
            }),
            errorMessage && 'border-red-500 dark:border-red-400',
            currency === 'PHP' && 'pl-8',
            currency === 'RP' && 'pl-11'
          )}
          disabled={disabled as boolean}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={onBlur}
          autoComplete="off"
        />
        {helper && (
          <p className="mt-2 text-left text-xs font-semibold text-gray-600 first-letter:capitalize">
            {helper}
          </p>
        )}
        {errorMessage && !hideErrorMessage && (
          <p className="mt-2 text-left text-xs font-semibold text-red-600 dark:text-red-400 first-letter:capitalize">
            {errorMessage}
          </p>
        )}
      </div>
    )
  }
)
